import React, {useCallback, useEffect, useState, useRef} from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import classnames from 'classnames';
import Classes from '../../../../common/Classes';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs';
import BigCircleSpinner from '../../../../common/components/BigCircleSpinner';
import PropTypes from 'prop-types';
import bundledAssets from '../../../../../images/assets.json';
import Image from 'react-bootstrap/lib/Image';
import { trackImpressions } from '../../../../common/MixpanelHelper';
import mixpanelConstants from '../../../../common/mixpanelConstants';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    pdfjsWorker,
    import.meta.url,
).toString();

const options = {
    cMapUrl: 'https://unpkg.com/pdfjs-dist@4.3.136/cmaps/',
    cMapPacked: true
};

const observerConfig = {
    root: null,
    threshold: 0
};

const useIntersectionObserver = (element, options, observerCallback) => {
    useEffect(() => {
        if (!element || !('IntersectionObserver' in window)) {
            return undefined;
        }

        const observer = new IntersectionObserver(observerCallback, options);

        const timeoutId = setTimeout(() => {
            observer.observe(element);
        }, 0);

        return () => {
            clearTimeout(timeoutId);
            observer.disconnect();
        };
    }, [element, options, observerCallback]);
}

const PageWithObserver= ({ pageNumber, setPageVisibility, ...otherProps }) => {
    const [page, setPage] = useState(null);

    const onIntersectionChange = useCallback(
        ([entry]) => {
            setPageVisibility(pageNumber, entry.isIntersecting);
        },
        [pageNumber, setPageVisibility]
    );

    useIntersectionObserver(page, observerConfig, onIntersectionChange);

    return <Page canvasRef={setPage} pageNumber={pageNumber} {...otherProps} loading={''} />;
}

const RenderPdf = (props) => {
    const [numPages, setNumPages] = useState(0);
    const [file, setFile] = useState(null);
    const pageHeight = 400; // The height of each page
    const pdfWidth = 720;
    const [visiblePages, setVisiblePages] = useState({});
    const [displayLoader, setDisplayLoader] = useState(true);
    const productRef = useRef();
    const mixpanelProperties = {
        ...props.mixpanelProps, 
        component: mixpanelConstants.component.exhibitorDirectoryOverlay,    
    };

    useEffect(() => {
        fetch(props.documentUrl)
            .then(response => response.blob())
            .then(blob => {
                setFile(URL.createObjectURL(blob));
            })
            .catch(error => console.error(error));
        
        delete mixpanelProperties.downloadUrl;
 
        const observer = trackImpressions(mixpanelConstants.eventName.documentImpression, mixpanelProperties);
    
        if (productRef.current) {
            observer.observe(productRef.current);
        }
    
        return () => {
            observer.disconnect();
        }
    }, []);

    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
        setTimeout(() => setDisplayLoader(false), 500);
    };

    const renderErrorSection = () => {
        return (<>
        <div className={Classes.documentErrorSection}>
            <Image responsive className={classnames(Classes.centerBlock)} src={bundledAssets.defaultDocumentImageUrl} />
            <div>{props.translations.modal.documentErrorTextLine1}</div>
            <div>{props.translations.modal.documentErrorTextLine2}</div>
        </div>
        </>)
    };

    const setPageVisibility = useCallback((pageNumber, isIntersecting) => {
        setVisiblePages((prevVisiblePages) => ({
            ...prevVisiblePages,
            [pageNumber]: isIntersecting
        }));
    }, []);

    return (
        <>
            <div className={Classes.pdfContainer} ref={productRef}>
            {displayLoader && !props.displayError ? <BigCircleSpinner /> : ''}
            {props.displayError ? renderErrorSection() : ''}

                <Document
                    options={options}
                    file={file}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={_error => {
                        props.disableDownload();
                    }}
                    onSourceError={_error => {
                        props.disableDownload();
                    }}
                    className={displayLoader ? classnames(Classes.hide) : classnames(Classes.pdfDocument)}
                >
                    {Array.from(
                        [...Array(numPages)],
                        (_el, index) => (
                        <PageWithObserver
                            key={`page_${index + 1}`}
                            pageNumber={index + 1}
                            setPageVisibility={setPageVisibility}
                            width={pdfWidth} height={pageHeight} scale={1}
                        />
                        ),
                    )}
                </Document>
                {
                    <p className={displayLoader || props.displayError ? classnames(Classes.hide) : classnames(Classes.pdfPager)}>
                        {props.translations.modal.page}{' '}
                        {Math.min(...Object.entries(visiblePages)
                            .filter(([_key, value]) => value)
                            .map(([key]) => parseInt(key, 10)))}
                        {' '}/{' '}{numPages}
                    </p>
                }
            </div>
        </>
    );
};

RenderPdf.propTypes = {
    documentUrl: PropTypes.string.isRequired,
    translations:PropTypes.object.isRequired,
    displayError: PropTypes.bool.isRequired,
    disableDownload: PropTypes.func.isRequired
};

export default RenderPdf;
